import { CANVAS_HEIGHT, CANVAS_WIDTH } from '~/design-system/components/DeckGen/types/presentation'

/** Longest side an inserted/pasted image is clamped to (px, slide-local). Echoes the text paste width. */
const IMAGE_ELEMENT_MAX_SIZE = 640

/** Fallback box for images with no intrinsic size (e.g. some SVGs report 0×0). */
const IMAGE_FALLBACK_SIZE = { width: 640, height: 360 } as const

/**
 * Cap the STORED pixel size of an inserted image to the slide resolution. Images are persisted as
 * inline dataURLs in the deck JSON (localStorage, ~5MB), so a raw 12MP photo would blow the quota.
 * Anything larger than this is re-encoded down via a canvas; this is the single biggest lever on
 * deck size and keeps decks under quota so they actually save.
 */
const IMAGE_MAX_STORED_DIM = 1920
const JPEG_REENCODE_QUALITY = 0.85

const IMAGE_URL_PATTERN = /^https?:\/\/[^\s]+\.(png|jpe?g|gif|webp|svg|avif)(\?[^\s]*)?$/i

/**
 * dataTransfer MIME the Library uses to drag image(s) onto the canvas. The payload is a JSON array of
 * image URLs (one for a single thumbnail, many for a multi-selection). InfiniteCanvas reads the same key
 * on drop and places them at the cursor.
 */
export const CANVAS_IMAGE_DRAG_MIME = 'application/x-deck-image-urls'

/** Default longest side for a vector (SVG) insert — SVGs are resolution-independent, so this is purely
 *  a sensible on-canvas display size, not a quality cap. The real aspect ratio comes from the markup. */
const SVG_INSERT_LONGEST_SIDE = 512

/** True if a source string is an SVG: an `image/svg+xml` data URL, or a URL ending in `.svg`. */
export function isSvgSource(src: string): boolean {
  const trimmed = src.trim()
  if (/^data:image\/svg\+xml/i.test(trimmed)) return true
  return /\.svg(\?[^\s]*)?$/i.test(trimmed)
}

/** True if a File is an SVG (by MIME, or by `.svg` name when the browser left the type blank). */
function isSvgFile(file: File): boolean {
  return file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)
}

/**
 * Derive an SVG's aspect ratio from its markup, since SVGs frequently report 0×0 to `<img>` (no intrinsic
 * size when width/height are omitted). Reads explicit `width`/`height` first, else the `viewBox` w/h.
 * Returns null when nothing usable is present (caller falls back to a square / default box).
 */
function parseSvgAspect(svgMarkup: string): { width: number; height: number } | null {
  const tag = svgMarkup.match(/<svg[^>]*>/i)?.[0]
  if (!tag) return null
  const num = (raw: string | undefined) => {
    if (!raw) return NaN
    const value = parseFloat(raw)
    return Number.isFinite(value) && value > 0 ? value : NaN
  }
  const w = num(tag.match(/\bwidth\s*=\s*["']?\s*([\d.]+)/i)?.[1])
  const h = num(tag.match(/\bheight\s*=\s*["']?\s*([\d.]+)/i)?.[1])
  if (Number.isFinite(w) && Number.isFinite(h)) return { width: w, height: h }
  const viewBox = tag.match(/viewBox\s*=\s*["']\s*[\d.+-]+[\s,]+[\d.+-]+[\s,]+([\d.+-]+)[\s,]+([\d.+-]+)/i)
  if (viewBox) {
    const vw = num(viewBox[1])
    const vh = num(viewBox[2])
    if (Number.isFinite(vw) && Number.isFinite(vh)) return { width: vw, height: vh }
  }
  return null
}

/** Box (longest side = {@link SVG_INSERT_LONGEST_SIDE}) for an SVG aspect, or a square fallback. */
function svgInsertSize(aspect: { width: number; height: number } | null): { width: number; height: number } {
  if (!aspect) return { width: SVG_INSERT_LONGEST_SIDE, height: SVG_INSERT_LONGEST_SIDE }
  const longest = Math.max(aspect.width, aspect.height)
  const scale = SVG_INSERT_LONGEST_SIDE / longest
  return {
    width: Math.max(1, Math.round(aspect.width * scale)),
    height: Math.max(1, Math.round(aspect.height * scale)),
  }
}

/** Decode the SVG markup out of a data URL (handles both base64 and percent-encoded payloads). */
function decodeSvgDataUrl(dataUrl: string): string | null {
  const match = dataUrl.match(/^data:image\/svg\+xml(;[^,]*)?,(.*)$/is)
  if (!match) return null
  const meta = match[1] ?? ''
  const payload = match[2]
  try {
    if (/;base64/i.test(meta)) return atob(payload)
    return decodeURIComponent(payload)
  } catch {
    return null
  }
}

/** A single, whitespace-free http(s) URL that points at an image file. */
export function isImageUrl(text: string): boolean {
  const trimmed = text.trim()
  if (trimmed.length === 0 || /\s/.test(trimmed)) return false
  return IMAGE_URL_PATTERN.test(trimmed)
}

/** Load an image URL just to read its intrinsic pixel size (no CORS needed to measure). Resolves null
 *  if it fails to load. Used by the web-card "extract images" filter to keep only large-enough images. */
export function measureImageUrl(url: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => resolve(null)
    img.src = url
  })
}

/** TLDs we DON'T treat as websites when a scheme is missing — i.e. bare filenames that look domain-ish. */
const NON_WEB_FILE_EXTENSIONS = new Set([
  'txt', 'md', 'markdown', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'ico',
  'js', 'ts', 'tsx', 'jsx', 'json', 'csv', 'zip', 'gz', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'html', 'htm', 'css', 'mp4', 'mov', 'mp3', 'wav', 'dmg', 'exe', 'pkg',
])

/** A single, whitespace-free URL pointing at a WEBSITE (not a direct image link) — pasted to embed as a
 *  web card. Accepts an explicit http(s) URL OR a bare domain like "granola.ai" / "www.dwellup.io/pricing"
 *  (the caller prepends https://). Bare tokens that are really filenames ("report.pdf") stay Notes. */
export function isWebUrl(text: string): boolean {
  const trimmed = text.trim()
  if (trimmed.length === 0 || /\s/.test(trimmed)) return false
  if (isImageUrl(trimmed)) return false
  // Explicit scheme: any http(s) URL with a dotted host.
  if (/^https?:\/\/[^\s.]+\.[^\s]+$/i.test(trimmed)) return true
  // Scheme-less bare domain: a dotted host token with an alphabetic TLD that isn't a common filename.
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,24}(\/\S*)?$/i.test(trimmed)) {
    const tld = trimmed.split('/')[0].split('.').pop()?.toLowerCase() ?? ''
    return !NON_WEB_FILE_EXTENSIONS.has(tld)
  }
  return false
}

/** Normalise a pasted website token to a full href (bare "granola.ai" → "https://granola.ai"). */
export function toWebHref(text: string): string {
  const trimmed = text.trim()
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

/** Sites commonly serve a DOWNSCALED image variant, which "extract images" would measure as too small and
 *  drop. Resolve to the full-resolution source instead:
 *   - Next.js `/_next/image?url=…&w=256` → the original behind the `url` param (the optimizer rejects
 *     arbitrary widths, so we bypass it entirely rather than bump `w`).
 *   - Generic CDNs with a `?w=`/`?width=` thumbnail param → request a large render (1920).
 *  The source is never upscaled past its native size, so this only ever helps. Unchanged if neither applies. */
export function upscaleImageUrl(url: string): string {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://x')
    // Next.js image optimizer: the real source is the `url` param — use it directly (no width cap, no 400).
    if (/\/_next\/image\/?$/i.test(u.pathname)) {
      const inner = u.searchParams.get('url')
      if (inner) return new URL(inner, u.origin).toString()
    }
    // Generic CDN width param → ask for a large render (a value most resizers accept).
    let touched = false
    for (const key of ['w', 'width']) {
      const raw = u.searchParams.get(key)
      if (raw == null) continue
      const value = parseInt(raw, 10)
      if (Number.isFinite(value) && value < 1600) {
        u.searchParams.set(key, '1920')
        touched = true
      }
    }
    return touched ? u.toString() : url
  } catch {
    return url
  }
}

/**
 * HEIC and HEIF, the format phones save photos in. The vision model refuses them, and only Safari can decode
 * one, so everywhere else (Chrome, the Mac app) a HEIC cannot be turned into something the model reads.
 * The file's type is empty in some browsers, so the name counts too.
 */
export function isHeicFile(file: File): boolean {
  return /^image\/hei[cf](-sequence)?$/i.test(file.type) || /\.hei[cf]$/i.test(file.name)
}

/** What the user is told when a HEIC photo cannot be used: plainly, that Deck does not cover it yet. */
export const HEIC_NOT_SUPPORTED = "HEIC photos aren't supported yet. Please use a JPEG or PNG."

/** Formats the vision analysis API reads natively (PNG, JPG, WebP, GIF). SVG and AVIF are NOT among them. */
const VISION_SUPPORTED_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp'])
/** When an SVG (or other vector) reports no intrinsic size, rasterize at this size so analysis still works. */
const RASTERIZE_FALLBACK = { width: 1024, height: 768 } as const

/**
 * Ensure an uploaded image is in a format the vision model can read. PNG / JPG / WebP / GIF pass through
 * untouched (no re-encode → no quality loss or content-hash churn). Everything else — SVG (vector, not a
 * raster the API accepts) and AVIF (which websites serve a lot, rejected with `invalid_image_format`) —
 * is rasterized to PNG via a canvas (the browser already decodes it, since it renders the thumbnail).
 * Falls back to the original file if decoding fails, so a quirky image still uploads (just without a
 * description).
 */
export async function ensureVisionCompatibleImageFile(file: File): Promise<File> {
  if (VISION_SUPPORTED_TYPES.has(file.type)) return file
  try {
    // SVGs need the xmlns namespace to load in an <img> at all — without it the browser fires onerror and
    // we'd fall back to the raw (broken) file, which then shows as a broken thumbnail in the Library. Repair
    // the markup (add xmlns, ensure a size) and rasterize from that so the upload is always a valid PNG.
    const dataUrl = isSvgFile(file)
      ? svgMarkupToDataUrl(repairSvgMarkup(await file.text()))
      : await readFileAsDataURL(file)
    const img = await loadImageElement(dataUrl)
    // SVGs frequently report 0×0 (no intrinsic size) — rasterize them at a sensible default instead.
    const w = img.naturalWidth || RASTERIZE_FALLBACK.width
    const h = img.naturalHeight || RASTERIZE_FALLBACK.height
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(img, 0, 0, w, h)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) return file
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
    return new File([blob], `${baseName}.png`, { type: 'image/png' })
  } catch {
    return file
  }
}

/**
 * Make SVG markup safe to render in an `<img>`/canvas: ensure the SVG namespace is present (Chrome fires
 * `onerror` on a namespace-less `<svg>`), and ensure it has a width/height OR viewBox so it has an
 * intrinsic size. Idempotent — markup that already has these is returned unchanged.
 */
function repairSvgMarkup(svgMarkup: string): string {
  const tag = svgMarkup.match(/<svg[^>]*>/i)?.[0]
  if (!tag) return svgMarkup
  let repaired = svgMarkup
  if (!/\bxmlns\s*=/i.test(tag)) {
    repaired = repaired.replace(/<svg/i, '<svg xmlns="http://www.w3.org/2000/svg"')
  }
  // If there's neither a viewBox nor an explicit size, give it a default box so it has an intrinsic size.
  const newTag = repaired.match(/<svg[^>]*>/i)?.[0] ?? ''
  const hasSize = /\bwidth\s*=/i.test(newTag) && /\bheight\s*=/i.test(newTag)
  if (!/viewBox\s*=/i.test(newTag) && !hasSize) {
    repaired = repaired.replace(/<svg/i, `<svg viewBox="0 0 ${RASTERIZE_FALLBACK.width} ${RASTERIZE_FALLBACK.height}"`)
  }
  return repaired
}

/** UTF-8-safe `image/svg+xml` data URL (base64) from raw markup. */
function svgMarkupToDataUrl(svgMarkup: string): string {
  const utf8 = typeof TextEncoder !== 'undefined'
    ? Array.from(new TextEncoder().encode(svgMarkup), (byte) => String.fromCharCode(byte)).join('')
    : unescape(encodeURIComponent(svgMarkup))
  return `data:image/svg+xml;base64,${btoa(utf8)}`
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result === 'string') resolve(result)
      else reject(new Error('Unexpected FileReader result'))
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/** Resolves the natural pixel size of an image source (dataURL or URL). Falls back when unknown. */
function loadImageNaturalSize(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        resolve({ width: img.naturalWidth, height: img.naturalHeight })
      } else {
        resolve({ ...IMAGE_FALLBACK_SIZE })
      }
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = src
  })
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = src
  })
}

/**
 * Reads an image file → (if oversized) re-encodes it smaller via a canvas → returns the dataURL to
 * store plus its pixel size. Keeps the deck's inline image payload bounded so a single paste can't
 * exceed the localStorage quota. PNG sources stay PNG (alpha preserved); JPEGs re-encode as JPEG.
 *
 * SVGs are special-cased: the original markup is kept verbatim (NEVER rasterized → stays crisp at any
 * zoom), and the box is sized from the SVG's width/height or viewBox (since SVGs often report 0×0 to
 * `<img>`). `isSvg` lets the renderer paint it `object-contain` over a transparent card.
 */
async function loadImageFileForInsert(
  file: File,
): Promise<{ src: string; width: number; height: number; isSvg?: boolean }> {
  const original = await readFileAsDataURL(file)
  if (isSvgFile(file)) {
    // Repair the markup (add xmlns / a viewBox) so it actually renders in an <img>, then keep it as the
    // vector source (never rasterized → crisp at any zoom). Size comes from the markup, since SVGs report 0×0.
    const repaired = repairSvgMarkup(decodeSvgDataUrl(original) ?? '')
    const aspect = parseSvgAspect(repaired)
    const size = svgInsertSize(aspect)
    return { src: svgMarkupToDataUrl(repaired), width: size.width, height: size.height, isSvg: true }
  }
  const img = await loadImageElement(original)
  const naturalW = img.naturalWidth || IMAGE_FALLBACK_SIZE.width
  const naturalH = img.naturalHeight || IMAGE_FALLBACK_SIZE.height
  const longest = Math.max(naturalW, naturalH)
  if (longest <= IMAGE_MAX_STORED_DIM) {
    return { src: original, width: naturalW, height: naturalH }
  }

  const scale = IMAGE_MAX_STORED_DIM / longest
  const width = Math.max(1, Math.round(naturalW * scale))
  const height = Math.max(1, Math.round(naturalH * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return { src: original, width: naturalW, height: naturalH }
  ctx.drawImage(img, 0, 0, width, height)
  const src =
    file.type === 'image/jpeg'
      ? canvas.toDataURL('image/jpeg', JPEG_REENCODE_QUALITY)
      : canvas.toDataURL('image/png')
  return { src, width, height }
}

/** Box sized to the image's aspect (longest side ≤ {@link IMAGE_ELEMENT_MAX_SIZE}) centered on the slide. */
export function computeImageInsertBox(
  naturalWidth: number,
  naturalHeight: number,
): { x: number; y: number; width: number; height: number } {
  const safeW = naturalWidth > 0 ? naturalWidth : IMAGE_FALLBACK_SIZE.width
  const safeH = naturalHeight > 0 ? naturalHeight : IMAGE_FALLBACK_SIZE.height
  const scale = Math.min(1, IMAGE_ELEMENT_MAX_SIZE / Math.max(safeW, safeH))
  const width = Math.max(8, Math.round(safeW * scale))
  const height = Math.max(8, Math.round(safeH * scale))
  const x = Math.min(Math.max(0, Math.round(CANVAS_WIDTH / 2 - width / 2)), CANVAS_WIDTH - width)
  const y = Math.min(Math.max(0, Math.round(CANVAS_HEIGHT / 2 - height / 2)), CANVAS_HEIGHT - height)
  return { x, y, width, height }
}

/**
 * The store action that creates an image element (typed locally to avoid a store import cycle).
 * May return a `swapSrc` handle for patching the created item's source later — used to replace a
 * just-pasted dataURL with its uploaded pool URL once the background upload completes.
 */
type AddImageElement = (args: {
  src: string
  naturalWidth: number
  naturalHeight: number
  isSvg?: boolean
  position?: { x: number; y: number }
}) => void | { swapSrc?: (hostedUrl: string) => void }

/**
 * Background uploader for deck-bound canvases: posts the file to the conversation's image pool
 * (same endpoint the Library upload tab uses) and resolves the hosted URL. Returns undefined when
 * there's no conversation (standalone /deck-gen) so inserts keep their local dataURLs.
 */
export function createPoolImageUploader(
  conversationId: string | undefined,
): ((file: File) => Promise<string>) | undefined {
  if (!conversationId) return undefined
  return async (file: File) => {
    const formData = new FormData()
    formData.append('file', await ensureVisionCompatibleImageFile(file))
    const res = await fetch(`/conversation-images-api/${encodeURIComponent(conversationId)}`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) throw new Error(`Pool upload failed: ${res.status}`)
    const data = (await res.json()) as { url?: string }
    if (!data.url) throw new Error('Pool upload returned no URL')
    return data.url
  }
}

/** When inserting several images at once, each is nudged by this many px so they don't stack exactly. */
const IMAGE_CASCADE_STEP = 24

/**
 * Read an image file → measure → insert. `cascadeOffset` nudges multi-insert so they don't overlap.
 *
 * When `uploadFile` is provided (deck-bound canvases with a conversation image pool), the image is
 * inserted instantly with its local dataURL, then uploaded in the background; on success the item's
 * src is swapped to the hosted URL so the persisted presentation stores a small URL instead of the
 * image bytes (assets never live in the document — see DECKGEN_CANVAS_SCALE_PLAN.md Stage 0).
 * SVGs are excluded: their inline vector markup is small and must stay crisp at any zoom.
 * Upload failure keeps the dataURL — degraded (heavy) but fully functional.
 */
export async function insertImageFromFile(
  file: File,
  add: AddImageElement,
  cascadeIndex = 0,
  uploadFile?: (file: File) => Promise<string>,
): Promise<boolean> {
  if (!file.type.startsWith('image/') && !isSvgFile(file)) return false
  try {
    const { src, width, height, isSvg } = await loadImageFileForInsert(file)
    const ref = add({ src, naturalWidth: width, naturalHeight: height, isSvg, position: cascadePosition(width, height, cascadeIndex) })
    if (uploadFile && !isSvg && ref && typeof ref === 'object' && ref.swapSrc) {
      const swapSrc = ref.swapSrc
      uploadFile(file)
        .then((hostedUrl) => swapSrc(hostedUrl))
        .catch((error) => console.warn('[DeckGen] pool upload failed — image stays inline (dataURL):', error))
    }
    return true
  } catch {
    return false
  }
}

/** Measure an image URL → insert it (keeps the URL as the source, no fetch/dataURL conversion). */
export async function insertImageFromUrl(
  url: string,
  add: AddImageElement,
  cascadeIndex = 0,
): Promise<boolean> {
  if (isSvgSource(url)) {
    // SVGs often report 0×0 to <img>, so derive the aspect from the markup. Best-effort fetch — if CORS
    // blocks it we still insert (keeping the URL as the vector source), just with a square default box.
    let size: { width: number; height: number } | null = null
    try {
      const markup = await fetch(url).then((r) => (r.ok ? r.text() : ''))
      size = svgInsertSizeOrNull(parseSvgAspect(markup))
    } catch {
      size = null
    }
    const box = size ?? { width: SVG_INSERT_LONGEST_SIDE, height: SVG_INSERT_LONGEST_SIDE }
    add({ src: url, naturalWidth: box.width, naturalHeight: box.height, isSvg: true, position: cascadePosition(box.width, box.height, cascadeIndex) })
    return true
  }
  try {
    const { width, height } = await loadImageNaturalSize(url)
    add({ src: url, naturalWidth: width, naturalHeight: height, position: cascadePosition(width, height, cascadeIndex) })
    return true
  } catch {
    return false
  }
}

/** Like {@link svgInsertSize} but returns null on no aspect, so the URL path can default to a square box. */
function svgInsertSizeOrNull(
  aspect: { width: number; height: number } | null,
): { width: number; height: number } | null {
  return aspect ? svgInsertSize(aspect) : null
}

function cascadePosition(width: number, height: number, offset: number): { x: number; y: number } | undefined {
  if (!offset) return undefined
  const box = computeImageInsertBox(width, height)
  return { x: box.x + offset * IMAGE_CASCADE_STEP, y: box.y + offset * IMAGE_CASCADE_STEP }
}
